import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export type ManualScrollRestorationOrAutoOptions = {
    paths: string[];
};

const handleLeavingManualPage = (): void => window.scrollTo(0, 0);
const handleLeavingAutoPage = (): void => undefined;

export const useManualScrollRestorationOrAuto = (
    options: ManualScrollRestorationOrAutoOptions,
): void => {
    const trackUnloadEventHandler = useRef<(() => void) | null>(null);
    const trackPreviousPath = useRef<string | null>(null);
    const { paths = [] } = options;
    const { pathname: currentPathname } = useLocation();

    useEffect(() => {
        if (currentPathname !== trackPreviousPath.current) {
            // We are opening a new page and the unload event handler is set.
            if (trackUnloadEventHandler.current) {
                // Call it since it means we are moving to a page that is also a react router page.
                trackUnloadEventHandler.current.call(null);
                window.removeEventListener('beforeunload', trackUnloadEventHandler.current);
            }

            // Logic when opening a new page.
            if (paths.some((path: string) => currentPathname?.startsWith(path))) {
                window.history.scrollRestoration = 'manual';
                trackUnloadEventHandler.current = handleLeavingManualPage;
            } else {
                window.history.scrollRestoration = 'auto';
                trackUnloadEventHandler.current = handleLeavingAutoPage;
            }

            // Logic when leaving the website.
            window.addEventListener('beforeunload', () =>
                trackUnloadEventHandler.current?.call(null),
            );

            trackPreviousPath.current = currentPathname;
        }
    }, [currentPathname, paths]);
};
